// Tablero del Buscaminas. Ratón: clic descubre, clic derecho pone bandera.
// Táctil: toque descubre (o pone bandera en modo bandera), pulsación larga pone bandera.
// Teclado: flechas para moverse, Intro o espacio descubre, F pone bandera.
import { memo, useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import type { GameState } from './engine';

export type CellAction = 'r' | 'f' | 'c';

const LONG_PRESS_MS = 380;

// Color de cada número: 1 azul, 2 verde, 3 rojo, 4 índigo, 5 naranja, 6 turquesa, 7 tinta, 8 gris
const NUMBER_COLORS = ['', 'text-accent', 'text-success', 'text-danger', 'text-mines-4', 'text-warning', 'text-mines-6', 'text-ink', 'text-ink-3'];

export function FlagMark({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.5 3.25 18.5 7.5l-8 4.25z" fill="currentColor" />
      <path d="M10.5 17V3.25M7.5 17.25h8M5.5 21.25h12" />
    </svg>
  );
}

export function MineMark({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" aria-hidden="true">
      <path d="M12 2.75v3M12 18.25v3M2.75 12h3M18.25 12h3M5.5 5.5l1.75 1.75M16.75 16.75l1.75 1.75M18.5 5.5l-1.75 1.75M7.25 16.75 5.5 18.5" />
      <circle cx="12" cy="12" r="6.25" fill="currentColor" stroke="none" />
      <circle cx="10" cy="10" r="1.6" className="fill-surface" stroke="none" />
    </svg>
  );
}

interface CellProps {
  index: number;
  open: boolean;
  flag: boolean;
  mine: boolean;
  count: number;
  status: GameState['status'];
  exploded: boolean;
  focused: boolean;
  size: number;
  delay: number | null; // ms de retraso de la animación de apertura (null = sin animar)
  label: string;
}

const Cell = memo(function Cell({ index, open, flag, mine, count, status, exploded, focused, size, delay, label }: CellProps) {
  const radius = Math.round(size * 0.22);
  const glyph = Math.round(size * 0.58);
  const style: CSSProperties = { width: size, height: size, borderRadius: radius };
  if (delay !== null) style.animationDelay = `${delay}ms`;

  const ended = status !== 'playing';
  const showMine = mine && (open || (status === 'lost' && !flag));
  const wrongFlag = flag && !mine && status === 'lost';
  const autoFlag = mine && status === 'won';

  let cls: string;
  let content: React.ReactNode = null;

  if (exploded) {
    cls = 'bg-danger text-white';
    content = <MineMark className="pointer-events-none" />;
  } else if (showMine) {
    cls = 'bg-ink/[0.07] text-ink';
    content = <MineMark className="pointer-events-none" />;
  } else if (open) {
    cls = `bg-transparent ${count ? NUMBER_COLORS[count] : ''} ${delay !== null ? 'mines-open' : ''}`;
    content = count ? count : null;
  } else {
    cls = `bg-raised shadow-[0_1px_2px_rgb(0_0_0/0.10)] ${ended ? '' : 'hover:bg-thumb active:scale-[0.92]'} transition-[transform,background-color] duration-100`;
    if (flag || autoFlag) content = <FlagMark className={`pointer-events-none ${wrongFlag ? 'text-ink-3' : 'text-danger'}`} />;
  }

  return (
    <button
      type="button"
      data-i={index}
      tabIndex={focused ? 0 : -1}
      aria-label={label}
      className={`relative grid place-items-center select-none font-bold tabular outline-offset-1 ${cls}`}
      style={{ ...style, fontSize: Math.round(size * 0.52), lineHeight: 1 }}
    >
      {content && <span className="grid place-items-center" style={{ width: glyph, height: glyph }}>{content}</span>}
      {wrongFlag && <span className="absolute inset-[18%] pointer-events-none" aria-hidden="true">
        <svg viewBox="0 0 24 24" className="w-full h-full text-danger" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M5 5l14 14M19 5 5 19" /></svg>
      </span>}
    </button>
  );
});

function cellLabel(state: GameState, i: number, cols: number): string {
  const pos = `Fila ${Math.floor(i / cols) + 1}, columna ${(i % cols) + 1}`;
  if (state.open[i]) {
    if (state.board.mines[i]) return `${pos}: mina`;
    const n = state.board.counts[i];
    return `${pos}: ${n === 0 ? 'vacía' : n === 1 ? '1 mina alrededor' : `${n} minas alrededor`}`;
  }
  return `${pos}: ${state.flags[i] ? 'bandera' : 'sin descubrir'}`;
}

export default function MinesBoard({ state, size, gap, flagMode, onAction }: {
  state: GameState;
  size: number;
  gap: number;
  flagMode: boolean;
  onAction: (a: CellAction, i: number) => void;
}) {
  const { cfg } = state.board;
  const [focus, setFocus] = useState(state.board.first);
  const gridRef = useRef<HTMLDivElement>(null);

  // Animación de apertura: las casillas recién abiertas aparecen en cascada desde la última tocada
  const prevOpen = useRef<boolean[] | null>(null);
  const origin = useRef(state.board.first);
  const delays = useRef<(number | null)[]>([]);
  if (prevOpen.current !== state.open) {
    const prev = prevOpen.current && prevOpen.current.length === state.open.length ? prevOpen.current : null;
    const ox = origin.current % cfg.cols;
    const oy = Math.floor(origin.current / cfg.cols);
    delays.current = state.open.map((o, i) => {
      if (!o || (prev && prev[i])) return prev ? null : delays.current[i] ?? null;
      const d = Math.max(Math.abs((i % cfg.cols) - ox), Math.abs(Math.floor(i / cfg.cols) - oy));
      return Math.min(d * 18, 360);
    });
    prevOpen.current = state.open;
  }

  // Nuevo tablero: el foco vuelve a la casilla inicial
  useEffect(() => { setFocus(state.board.first); origin.current = state.board.first; }, [state.board]);

  // Pulsación larga (solo táctil)
  const press = useRef<{ i: number; timer: number; fired: boolean; touch: boolean } | null>(null);

  const cellFrom = (target: EventTarget | null): number | null => {
    const el = (target as HTMLElement | null)?.closest('[data-i]') as HTMLElement | null;
    return el ? Number(el.dataset.i) : null;
  };

  const act = (primary: boolean, i: number) => {
    origin.current = i;
    setFocus(i);
    if (state.open[i]) return onAction('c', i);  // en un número: abrir alrededor
    if (!primary || flagMode) return onAction('f', i);
    if (!state.flags[i]) onAction('r', i);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    const i = cellFrom(e.target);
    if (i === null || e.button !== 0) return;
    const touch = e.pointerType !== 'mouse';
    const timer = touch
      ? window.setTimeout(() => {
          if (!press.current) return;
          press.current.fired = true;
          if (!state.open[i]) {
            navigator.vibrate?.(12);
            act(false, i);
          }
        }, LONG_PRESS_MS)
      : 0;
    press.current = { i, timer, fired: false, touch };
  };

  const cancelPress = () => {
    if (press.current) window.clearTimeout(press.current.timer);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const p = press.current;
    press.current = null;
    if (!p) return;
    window.clearTimeout(p.timer);
    if (p.fired || e.button !== 0) return;
    const i = cellFrom(e.target);
    if (i === p.i) act(true, i);
  };

  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    const p = press.current;
    if (p?.touch) return; // en táctil manda la pulsación larga
    const i = cellFrom(e.target);
    if (i !== null) act(false, i);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    const x = focus % cfg.cols;
    const y = Math.floor(focus / cfg.cols);
    let next = focus;
    switch (e.key) {
      case 'ArrowLeft': next = y * cfg.cols + Math.max(0, x - 1); break;
      case 'ArrowRight': next = y * cfg.cols + Math.min(cfg.cols - 1, x + 1); break;
      case 'ArrowUp': next = Math.max(0, y - 1) * cfg.cols + x; break;
      case 'ArrowDown': next = Math.min(cfg.rows - 1, y + 1) * cfg.cols + x; break;
      case 'Enter':
      case ' ': e.preventDefault(); act(true, focus); return;
      case 'f':
      case 'F': e.preventDefault(); act(false, focus); return;
      default: return;
    }
    e.preventDefault();
    setFocus(next);
    gridRef.current?.querySelector<HTMLElement>(`[data-i="${next}"]`)?.focus();
  };

  return (
    <div
      ref={gridRef}
      role="grid"
      aria-label={`Tablero de ${cfg.cols} por ${cfg.rows} con ${cfg.mines} minas`}
      className="grid touch-manipulation"
      style={{ gridTemplateColumns: `repeat(${cfg.cols}, ${size}px)`, gap, WebkitTouchCallout: 'none' } as CSSProperties}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerLeave={cancelPress}
      onPointerCancel={() => { cancelPress(); press.current = null; }}
      onContextMenu={onContextMenu}
      onKeyDown={onKeyDown}
    >
      {state.open.map((open, i) => (
        <Cell
          key={i}
          index={i}
          open={open}
          flag={state.flags[i]}
          mine={state.board.mines[i]}
          count={state.board.counts[i]}
          status={state.status}
          exploded={state.exploded === i}
          focused={focus === i}
          size={size}
          delay={delays.current[i] ?? null}
          label={cellLabel(state, i, cfg.cols)}
        />
      ))}
    </div>
  );
}
